import React from 'react';
import { Tooltip } from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

export interface InfoHintProps {
  /** The explanation shown on hover or focus. */
  title: React.ReactNode;
  /** Names the icon for screen readers, e.g. "About approval rate". */
  label: string;
  /**
   * `neutral` for a plain definition, kept quiet. `warning` / `danger` when
   * what it says changes how the figure should be read, so the icon itself
   * draws the eye before anyone hovers.
   */
  tone?: 'neutral' | 'warning' | 'danger';
}

/**
 * The small info icon that explains the thing beside it: what a metric
 * means, how a figure is derived, what to be careful of. One pattern for
 * every explanation — metric cards, panels, table rows and table groups —
 * so a reader learns it once.
 *
 * Reachable by keyboard, and shown on focus as well as hover.
 */
export function InfoHint({ title, label, tone = 'neutral' }: InfoHintProps) {
  return (
    <Tooltip
      title={title}
      placement="top"
      slotProps={{
        // A long explanation must stay on the page: it flips or slides to fit,
        // keeps a margin from every edge, and scrolls inside itself rather
        // than running off the top where it cannot be read.
        popper: {
          modifiers: [
            { name: 'preventOverflow', options: { padding: 12, altAxis: true, tether: false } },
            { name: 'flip', options: { padding: 12 } },
          ],
        },
        tooltip: { sx: { maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', overscrollBehavior: 'contain' } },
      }}
    >
      <InfoOutlined
        tabIndex={0}
        aria-label={label}
        sx={(t) => ({
          flexShrink: 0,
          fontSize: t.typography.body2.fontSize,
          cursor: 'help',
          color:
            tone === 'danger'
              ? t.tokens.semantic.danger
              : tone === 'warning' ? t.tokens.semantic.warning : t.tokens.text.tertiary,
          '&:hover': { color: tone === 'neutral' ? t.tokens.text.accent : undefined },
          '&:focus-visible': { outline: `2px solid ${t.tokens.border.focus}`, borderRadius: '50%' },
        })}
      />
    </Tooltip>
  );
}
